/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from 'react';
import { Link } from 'react-router';
import { Button } from '@letsdothis/lds/lib/Button';
import {
  LinkButton,
  LinkButtonType,
} from '@letsdothis/lds/lib/candidate/LinkButton';
import { useStyletron } from 'baseui';

import { useAuthService } from '@/contexts/authService';
import { logError } from '@/utils/logError';

import { STAGES } from '../../enums';
import Field from '../../../Form/Fields/Field';
import DoubleField from '../../../Form/Fields/DoubleField';
import { FieldParams } from '../../../Form/Fields/Types';
import FieldValidators from '../FieldValidators';
import { validateSignUpParams, validateRegisterParams } from './Validator';
import * as styles from '../styles.module.css';
import { SignUpParams, RegisterParams } from './Types';

const register = async (
  authService: any,
  params: RegisterParams,
): Promise<void> => {
  validateRegisterParams(params);
  const {
    email,
    previousEmailError,
    givenName,
    previousGivenNameError,
    familyName,
    previousFamilyNameError,
    password,
    previousPasswordError,
    setState,
  } = params;

  const emailError = FieldValidators.validateEmail(email);
  if (emailError !== previousEmailError) {
    setState({ emailError });
  }
  const givenNameError = FieldValidators.validateGivenName(givenName);
  if (givenNameError !== previousGivenNameError) {
    setState({ givenNameError });
  }
  const familyNameError = FieldValidators.validateFamilyName(familyName);
  if (familyNameError !== previousFamilyNameError) {
    setState({ familyNameError });
  }
  const passwordError = FieldValidators.validatePassword(password);
  if (passwordError !== previousPasswordError) {
    setState({ passwordError });
  }

  if (!emailError && !givenNameError && !familyNameError && !passwordError) {
    // TODO: Redirect to modal about email verification.
    await authService.signUpEmailPassword({
      email,
      givenName,
      familyName,
      password,
    });
  } else {
    throw new Error('Invalid signup parameters');
  }
};

const SignUp = (params: SignUpParams): JSX.Element => {
  const [css, theme] = useStyletron();
  const authService = useAuthService();
  validateSignUpParams(params);
  const {
    email,
    emailError,
    givenName,
    givenNameError,
    familyName,
    familyNameError,
    password,
    passwordError,
    setStage,
    setState,
    signInOrUpButtonLoading,
    signInOrUpError,
  } = params;

  const setIsSigningUp = (isSigningUp: boolean) => setState({ isSigningUp });
  const setEmail = (newEmail: string) => setState({ email: newEmail });
  const setGivenName = (newGivenName: string) =>
    setState({ givenName: newGivenName });
  const setFamilyName = (newFamilyName: string) =>
    setState({ familyName: newFamilyName });
  const setPassword = (newPassword: string) =>
    setState({ password: newPassword });

  const emailFieldParams: FieldParams = {
    title: 'Email',
    value: email,
    setValue: setEmail,
    error: emailError,
  };
  const givenNameFieldParams: FieldParams = {
    title: 'First Name',
    value: givenName,
    setValue: setGivenName,
    error: givenNameError,
  };
  const familyNameFieldParams: FieldParams = {
    title: 'Last Name',
    value: familyName,
    setValue: setFamilyName,
    error: familyNameError,
  };
  const passwordFieldParams: FieldParams = {
    title: 'Password',
    hidden: true,
    value: password,
    setValue: setPassword,
    error: passwordError,
  };

  return (
    <div className={styles.emailPasswordContainer}>
      {Field(emailFieldParams)}
      {DoubleField(givenNameFieldParams, familyNameFieldParams)}
      {Field(passwordFieldParams)}
      <Button
        isLoading={signInOrUpButtonLoading}
        onClick={() => {
          setState({ signInOrUpButtonLoading: true });
          setState({ signInOrUpError: undefined });
          register(authService, {
            email,
            previousEmailError: emailError,
            givenName,
            previousGivenNameError: givenNameError,
            familyName,
            previousFamilyNameError: familyNameError,
            password,
            previousPasswordError: passwordError,
            setState,
          })
            .then(() => {
              setState({ signInOrUpButtonLoading: false });
              setStage(STAGES.SIGN_UP_COMPLETE);
            })
            .catch((error) => {
              setState({ signInOrUpButtonLoading: false });
              setState({
                signInOrUpError: 'Invalid password or email. Please try again.',
              });
              logError(error, {
                errorMessageFallback: 'Error while signing up.',
              });
            });
        }}
      >
        Sign up with email
      </Button>
      {signInOrUpError && (
        <div className={css({ color: theme.colors.negative400 })}>
          {signInOrUpError}
        </div>
      )}
      <div
        className={styles.signUpSignInSwitchText}
        onClick={() => setIsSigningUp(false)}
      >
        Already have an account?
        <LinkButton
          onClick={() => setState({ isSigningUp: false })}
          type={LinkButtonType.Label}
        >
          <div className={styles.linkText}>Log In</div>
        </LinkButton>
      </div>
      <div className={styles.terms}>
        By creating an account, you accept our <Link to="/terms">terms</Link>
      </div>
    </div>
  );
};

export default SignUp;
